import React from 'react';
import type {PrimitiveRecord, ReportError} from '../utils/errorHandling';
import {useReportError} from './ReportError';

export function firmAssert(
  reportError: ReportError,
  condition: boolean,
  message: string,
  owner: string,
  // eslint-disable-next-line default-param-last
  tags: PrimitiveRecord = {},
  extra?: PrimitiveRecord,
) {
  if (condition) {
    return;
  }

  try {
    throw new Error(`[${owner}]: ${message}`);
  } catch (err) {
    // This probably can't happen, but it makes TypeScript upset otherwise.
    const error = err instanceof Error ? err : Error(String(err));

    reportError(
      error,
      {
        ...tags,
        assertionOwner: owner,
        type: 'assertion',
      },
      extra,
    );
  }
}

export function softAssert(
  reportError: ReportError,
  condition: boolean,
  message: string,
  owner: string,
  // eslint-disable-next-line default-param-last
  tags: PrimitiveRecord = {},
  extra?: PrimitiveRecord,
) {
  return firmAssert(
    reportError,
    condition,
    message,
    owner,
    {
      ...tags,
      error_level: 'warning',
    },
    extra,
  );
}

export function assertFailed(
  reportError: ReportError,
  message: string,
  owner: string,
  // eslint-disable-next-line default-param-last
  tags: PrimitiveRecord = {},
  extra?: PrimitiveRecord,
) {
  return firmAssert(reportError, false, message, owner, tags, extra);
}

export function softAssertFailed(
  reportError: ReportError,
  message: string,
  owner: string,
  // eslint-disable-next-line default-param-last
  tags: PrimitiveRecord = {},
  extra?: PrimitiveRecord,
) {
  return softAssert(reportError, false, message, owner, tags, extra);
}

type AssertFailedFn = (
  message: string,
  owner: string,
  tags?: PrimitiveRecord,
  extra?: PrimitiveRecord,
) => void;
type AssertFn = (
  condition: boolean,
  ...rest: Parameters<AssertFailedFn>
) => void;

export interface AssertContext {
  firmAssert: AssertFn;
  softAssert: AssertFn;
  assertFailed: AssertFailedFn;
  softAssertFailed: AssertFailedFn;
  reportError: ReportError;
}

export function makeAssertions(reportError: ReportError): AssertContext {
  return {
    reportError,
    assertFailed: assertFailed.bind(undefined, reportError),
    firmAssert: firmAssert.bind(undefined, reportError),
    softAssert: softAssert.bind(undefined, reportError),
    softAssertFailed: softAssertFailed.bind(undefined, reportError),
  };
}

export function useAssertions(): AssertContext {
  const reportError = useReportError();
  return React.useMemo(() => makeAssertions(reportError), [reportError]);
}
